import proj4 from 'proj4';

export type IndexWindow = {start: number; stop: number};

export type Grid = {
  /** Cell-center coordinates along y (latitude or projected y) and x. */
  yCoords: Float64Array;
  xCoords: Float64Array;
};

function cellSpacing(coords: Float64Array): number {
  return (coords[coords.length - 1] - coords[0]) / (coords.length - 1);
}

/**
 * Index range of cells whose extent intersects [min, max] along a uniform coordinate
 * axis, padded by one cell on each side, in either ascending or descending order.
 * Returns a half-open [start, stop) window clamped to the axis.
 */
export function indexWindowForRange(
  coords: Float64Array,
  min: number,
  max: number,
  padCells: number = 1
): IndexWindow {
  const spacing = cellSpacing(coords);
  const toIndex = (value: number) => (value - coords[0]) / spacing;
  const [a, b] = [toIndex(min), toIndex(max)].sort((p, q) => p - q);
  // Cell i spans fractional index [i-0.5, i+0.5), so the intersecting cells are
  // ceil(a-0.5) .. floor(b+0.5).
  const start = Math.max(0, Math.ceil(a - 0.5) - padCells);
  const stop = Math.min(coords.length, Math.floor(b + 0.5) + padCells + 1);
  if (start >= stop) {
    // The geometry lies entirely outside the grid (e.g. a site outside HRRR's
    // CONUS coverage). Callers treat this as "dataset unavailable here".
    return {start: 0, stop: 0};
  }
  return {start, stop};
}

export type Projector = (lonLat: [number, number]) => [number, number];

/**
 * WGS84 → projected-grid converter from the CRS attributes rioxarray writes onto the
 * store's spatial_ref variable. proj4 accepts both WKT (crs_wkt) and proj strings.
 */
export function makeProjector(spatialRefAttrs: Record<string, unknown>): Projector {
  const crs =
    (spatialRefAttrs.proj4 as string | undefined) ??
    (spatialRefAttrs.crs_wkt as string | undefined) ??
    (spatialRefAttrs.spatial_ref as string | undefined);
  if (!crs) {
    throw new Error('spatial_ref has no proj4/crs_wkt attribute');
  }
  const convert = proj4('EPSG:4326', crs);
  return (lonLat) => convert.forward(lonLat) as [number, number];
}
